import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "../components/dashboard/dashboard-layout";
import { PageHeader } from "../components/dashboard/page-header";
import ProtectedRoute from "../components/ProtectedRoute";
import { useAuth } from "../context/AuthContext";
import apiClient from "../services/api";
import {
  Settings,
  Plus,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Zap,
  ShoppingBag,
  Star,
  MessageSquare,
  Instagram,
  Globe,
  Link,
  Shield,
  Clock,
  Users,
  Activity,
  ArrowRight,
  Eye,
  EyeOff
} from "lucide-react";

import "../styles/animations.css";

// Types for connection data
interface ConnectionData {
  connected: boolean;
  canRemove: boolean;
  data: any[];
  type: string;
  name: string;
  description: string;
}

interface ConnectionsData {
  connections: {
    shopify: ConnectionData;
    judgeme: ConnectionData;
    yotpo: ConnectionData;
    instagram: ConnectionData;
  };
  userEmail: string;
  totalConnections: number;
}

function ConnectionsContent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [connectionsData, setConnectionsData] = useState<ConnectionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [animateCards, setAnimateCards] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);
  const [removingPlatform, setRemovingPlatform] = useState<string | null>(null);

  // Handle OAuth success/error messages
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success) {
      // Show success message
      console.log('✅ Connection successful:', success);
      // You can add a toast notification here
    }
    
    if (error) {
      // Show error message
      console.log('❌ Connection failed:', error);
      // You can add a toast notification here
    }
  }, [searchParams]);

  // Fetch connections data
  const fetchConnectionsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/api/connections');

      if (response.data.success) {
        setConnectionsData(response.data.data);
        console.log('✅ Connections data loaded:', response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch connections');
      }
    } catch (error: any) {
      console.error('❌ Error fetching connections:', error);
      setError(error.message || 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  // Connect to a platform
  const handleConnect = async (platform: string) => {
    try {
      setConnectingPlatform(platform);

      // Get OAuth URL
      const response = await apiClient.get(`/api/connections/${platform}/oauth-url`);
      
      if (response.data.success) {
        // Redirect to OAuth URL
        window.location.href = response.data.data.oauthUrl;
      } else {
        throw new Error(response.data.message || 'Failed to get OAuth URL');
      }
    } catch (error: any) {
      console.error(`❌ Error connecting to ${platform}:`, error);
      setError(error.message || `Failed to connect to ${platform}`);
    } finally {
      setConnectingPlatform(null);
    }
  };

  // Remove a connection
  const handleRemove = async (platform: string) => {
    if (!confirm(`Are you sure you want to remove the ${platform} connection?`)) {
      return;
    }

    try {
      setRemovingPlatform(platform);

      const response = await apiClient.delete(`/api/connections/${platform}`);
      
      if (response.data.success) {
        // Refresh connections data
        await fetchConnectionsData();
        console.log(`✅ ${platform} connection removed`);
      } else {
        throw new Error(response.data.message || 'Failed to remove connection');
      }
    } catch (error: any) {
      console.error(`❌ Error removing ${platform} connection:`, error);
      setError(error.message || `Failed to remove ${platform} connection`);
    } finally {
      setRemovingPlatform(null);
    }
  };

  // Test a connection
  const handleTest = async (platform: string) => {
    try {
      const response = await apiClient.post(`/api/connections/test/${platform}`);
      
      if (response.data.success) {
        console.log(`✅ ${platform} connection test successful`);
        // You can add a toast notification here
      } else {
        throw new Error(response.data.message || 'Connection test failed');
      }
    } catch (error: any) {
      console.error(`❌ ${platform} connection test failed:`, error);
      // You can add a toast notification here
    }
  };

  // Trigger animations and fetch data when component mounts
  useEffect(() => {
    setAnimateCards(true);
    fetchConnectionsData();
  }, []);

  // Refresh data function
  const handleRefresh = () => {
    fetchConnectionsData();
  };

  // Get platform icon
  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'shopify':
        return <ShoppingBag className="h-6 w-6" />;
      case 'judgeme':
        return <Star className="h-6 w-6" />;
      case 'yotpo':
        return <MessageSquare className="h-6 w-6" />;
      case 'instagram':
        return <Instagram className="h-6 w-6" />;
      default:
        return <Globe className="h-6 w-6" />;
    }
  };

  // Get platform color
  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case 'shopify':
        return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'judgeme':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'yotpo':
        return 'text-purple-600 bg-purple-100 dark:bg-purple-900/20 dark:text-purple-400';
      case 'instagram':
        return 'text-pink-600 bg-pink-100 dark:bg-pink-900/20 dark:text-pink-400';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-white">
        <DashboardLayout>
          <div className="flex-1 overflow-hidden h-full">
            <main className="p-4 md:p-6 max-w-7xl mx-auto">
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto mb-4"></div>
                  <p className="text-gray-500 dark:text-gray-400">Loading connections...</p>
                </div>
              </div>
            </main>
          </div>
        </DashboardLayout>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-white">
        <DashboardLayout>
          <div className="flex-1 overflow-hidden h-full">
            <main className="p-4 md:p-6 max-w-7xl mx-auto">
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="text-red-500 mb-4">
                    <XCircle className="h-12 w-12 mx-auto" />
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
                  <button
                    onClick={handleRefresh}
                    className="px-4 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </main>
          </div>
        </DashboardLayout>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-white">
      <DashboardLayout>
        <div className="flex-1 overflow-hidden h-full">
          <main className="p-4 md:p-6 max-w-7xl mx-auto">
            {/* Page Header */}
            <PageHeader
              title="Platform Connections"
              description="Manage your integrations with review platforms and social media."
              onRefresh={handleRefresh}
            />

            {/* Connection Stats */}
            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-3 mb-6 md:mb-8">
              <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md transition-all duration-300 ${animateCards ? 'animate-fadeIn delay-100' : 'opacity-0'}`}>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Connections</h3>
                    <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
                      <Link className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </div>
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold">{connectionsData?.totalConnections || 0}</span>
                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">/ 4 platforms</span>
                  </div>
                </div>
              </div>

              <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md transition-all duration-300 ${animateCards ? 'animate-fadeIn delay-200' : 'opacity-0'}`}>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Integrations</h3>
                    <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
                      <Activity className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </div>
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {connectionsData ? Object.values(connectionsData.connections).filter(c => c.connected).length : 0}
                    </span>
                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">working</span>
                  </div>
                </div>
              </div>

              <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md transition-all duration-300 ${animateCards ? 'animate-fadeIn delay-300' : 'opacity-0'}`}>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Account</h3>
                    <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
                      <Users className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 truncate">
                    {connectionsData?.userEmail || user?.email || 'Not available'}
                  </div>
                </div>
              </div>
            </div>

            {/* Platform Connections Grid */}
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2">
              {connectionsData && Object.entries(connectionsData.connections).map(([platform, connection], index) => (
                <div
                  key={platform}
                  className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 ${animateCards ? `animate-fadeIn delay-${(index + 4) * 100}` : 'opacity-0'}`}
                >
                  <div className="p-6">
                    {/* Platform Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-xl ${getPlatformColor(platform)}`}>
                          {getPlatformIcon(platform)}
                        </div>
                        <div>
                          <h3 className="text-lg font-bold">{connection.name}</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{connection.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {connection.connected ? (
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-full text-xs font-medium">
                            <CheckCircle className="h-3 w-3" />
                            Connected
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full text-xs font-medium">
                            <XCircle className="h-3 w-3" />
                            Not Connected
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Connection Details */}
                    {connection.connected && connection.data && connection.data.length > 0 && (
                      <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Connection Details:</div>
                        {platform === 'shopify' && connection.data[0] && (
                          <div className="space-y-1">
                            <div className="text-sm font-medium">{connection.data[0].shop_domain || 'Shopify Store'}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Connected: {new Date(connection.data[0].connected_at || Date.now()).toLocaleDateString()}
                            </div>
                          </div>
                        )}
                        {platform === 'judgeme' && connection.data[0] && (
                          <div className="space-y-1">
                            <div className="text-sm font-medium">{connection.data[0].shop_domain || 'Judge.me Account'}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Connected: {new Date(connection.data[0].connected_at || Date.now()).toLocaleDateString()}
                            </div>
                          </div>
                        )}
                        {platform === 'yotpo' && connection.data[0] && (
                          <div className="space-y-1">
                            <div className="text-sm font-medium">{connection.data[0].app_key || 'Yotpo Account'}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Connected: {new Date(connection.data[0].connected_at || Date.now()).toLocaleDateString()}
                            </div>
                          </div>
                        )}
                        {platform === 'instagram' && connection.data && Array.isArray(connection.data) && connection.data.length > 0 && (
                          <div className="space-y-1">
                            <div className="text-sm font-medium">{connection.data[0]?.username || connection.data[0]?.Username || 'Instagram Account'}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Connected: {new Date(connection.data[0]?.connected_at || connection.data[0]?.createdAt || Date.now()).toLocaleDateString()}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Platform Type Badge */}
                    <div className="mb-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        connection.type === 'ecommerce' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' :
                        connection.type === 'reviews' ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400' :
                        connection.type === 'social' ? 'bg-pink-100 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400' :
                        'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                      }`}>
                        {connection.type === 'ecommerce' && <ShoppingBag className="h-3 w-3" />}
                        {connection.type === 'reviews' && <Star className="h-3 w-3" />}
                        {connection.type === 'social' && <MessageSquare className="h-3 w-3" />}
                        {connection.type.charAt(0).toUpperCase() + connection.type.slice(1)}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      {connection.connected ? (
                        <>
                          <button
                            onClick={() => handleTest(platform)}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                          >
                            <Zap className="h-4 w-4" />
                            Test Connection
                          </button>
                          {connection.canRemove && (
                            <button
                              onClick={() => handleRemove(platform)}
                              disabled={removingPlatform === platform}
                              className="flex items-center justify-center gap-2 px-3 py-2 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {removingPlatform === platform ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                              Remove
                            </button>
                          )}
                          {!connection.canRemove && (
                            <div className="flex items-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-lg text-sm">
                              <Shield className="h-4 w-4" />
                              Protected
                            </div>
                          )}
                        </>
                      ) : (
                        <button
                          onClick={() => handleConnect(platform)}
                          disabled={connectingPlatform === platform || platform === 'shopify'}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {connectingPlatform === platform ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                          {platform === 'shopify' ? 'Auto-Connected' : 'Connect'}
                          {connectingPlatform !== platform && <ExternalLink className="h-3 w-3" />}
                        </button>
                      )}
                    </div>

                    {/* Special Notes */}
                    {platform === 'shopify' && (
                      <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-blue-700 dark:text-blue-300">
                            Shopify connection is automatically managed and cannot be removed.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Help Section */}
            <div className={`mt-8 bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-800 ${animateCards ? 'animate-fadeIn delay-800' : 'opacity-0'}`}>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gray-100 dark:bg-gray-800">
                  <Settings className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-2">Need Help with Connections?</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Learn how to set up and manage your platform integrations for automated review responses.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-sm font-medium">
                      <ExternalLink className="h-4 w-4" />
                      Setup Guide
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-sm font-medium">
                      <MessageSquare className="h-4 w-4" />
                      Contact Support
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </DashboardLayout>
    </div>
  );
}

export default function ConnectionsPage() {
  return (
    <ProtectedRoute>
      <ConnectionsContent />
    </ProtectedRoute>
  );
}
